package br.com.vvrefeicoes.portal.feature.notifications

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.staticCompositionLocalOf
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.AppNotification
import br.com.vvrefeicoes.portal.domain.model.NotificationPriority
import br.com.vvrefeicoes.portal.domain.repository.NotificationRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/**
 * Estado compartilhado das notificações do usuário: lista, contagem de não lidas e as superfícies
 * globais (banner de média prioridade e modal de alta prioridade).
 */
class NotificationsController(
    private val repository: NotificationRepository,
    private val socket: NotificationStream,
    private val scope: CoroutineScope,
) {
    var items by mutableStateOf<List<AppNotification>>(emptyList())
        private set

    var unreadCount by mutableStateOf(0)
        private set

    var loading by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var banner by mutableStateOf<AppNotification?>(null)
        private set

    var modal by mutableStateOf<AppNotification?>(null)
        private set

    var query by mutableStateOf("")
        private set

    var page by mutableStateOf(0)
        private set

    var period by mutableStateOf(PeriodSelection.current(SystemDateTimeProvider().currentPeriod()).copy(mode = PeriodMode.TOTAL))
        private set

    var totalPages by mutableStateOf(1)
        private set

    val pageCount: Int
        get() = maxOf(1, totalPages)

    private var loadJob: Job? = null

    private var started = false

    fun init() {
        if (started) return
        started = true
        refresh()
        socket.start(scope, ::onIncoming)
        scope.launch {
            while (true) {
                delay(POLL_MILLIS)
                refresh(silent = true)
            }
        }
    }

    fun updateQuery(value: String) {
        query = value
        page = 0
        load(debounce = true)
    }

    fun applyPeriod(selection: PeriodSelection) {
        period = selection
        page = 0
        load()
    }

    fun goToPage(value: Int) {
        val target = value.coerceIn(0, pageCount - 1)
        if (target == page) return
        page = target
        load()
    }

    fun refresh(silent: Boolean = false) {
        load(silent = silent)
    }

    private fun load(
        debounce: Boolean = false,
        silent: Boolean = false,
    ) {
        loadJob?.cancel()
        loadJob =
            scope.launch {
                if (debounce) delay(SEARCH_DEBOUNCE_MS)
                if (!silent) loading = true
                try {
                    val feed = repository.loadFeed(page, PAGE_SIZE, query, period)
                    items = feed.items
                    unreadCount = feed.unreadCount
                    totalPages = feed.totalPages
                    errorMessage = null
                    evaluateSurfaces()
                } catch (exception: CancellationException) {
                    throw exception
                } catch (throwable: Throwable) {
                    if (!silent) {
                        errorMessage = throwable.message ?: "Não foi possível carregar as notificações."
                    }
                } finally {
                    if (!silent) loading = false
                }
            }
    }

    fun open(id: String) {
        val item = items.firstOrNull { it.id == id } ?: return
        if (item.read) return
        items = items.map { if (it.id == id) it.copy(read = true) else it }
        unreadCount = (unreadCount - 1).coerceAtLeast(0)
        scope.launch { runCatching { repository.markRead(id) } }
    }

    fun dismissBanner() {
        val current = banner
        if (current != null) {
            items = items.map { if (it.id == current.id) it.copy(read = true, dismissed = true) else it }
            scope.launch { runCatching { repository.dismiss(current.id) } }
        }
        banner = null
        evaluateSurfaces()
    }

    fun dismissModal() {
        val current = modal
        if (current != null) {
            items = items.map { if (it.id == current.id) it.copy(read = true, dismissed = true) else it }
            scope.launch { runCatching { repository.dismiss(current.id) } }
        }
        modal = null
        evaluateSurfaces()
    }

    private fun onIncoming(incoming: AppNotification) {
        items = listOf(incoming) + items.filterNot { it.id == incoming.id }
        if (!incoming.read) unreadCount += 1
        if (incoming.dismissed) return
        when (incoming.priority) {
            NotificationPriority.HIGH -> modal = incoming
            NotificationPriority.MEDIUM -> if (modal == null && banner == null) banner = incoming
            NotificationPriority.LOW -> Unit
        }
    }

    private fun evaluateSurfaces() {
        if (modal == null) {
            modal = items.firstOrNull { it.priority == NotificationPriority.HIGH && !it.dismissed }
        }
        if (modal == null && banner == null) {
            banner = items.firstOrNull { it.priority == NotificationPriority.MEDIUM && !it.dismissed }
        }
    }

    companion object {
        const val POLL_MILLIS = 60_000L
        const val PAGE_SIZE = 20
        const val SEARCH_DEBOUNCE_MS = 300L
    }
}

val LocalNotifications =
    staticCompositionLocalOf<NotificationsController> {
        error("NotificationsController não foi fornecido.")
    }

@Composable
fun rememberNotificationsController(): NotificationsController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        NotificationsController(
            repository = appGraph.notificationRepository,
            socket = appGraph.notificationSocket,
            scope = scope,
        )
    }
}
